/**
 * Cosmetic Prop Classification
 *
 * Classifies section property changes as 'cosmetic' or 'structural'.
 * Cosmetic changes can be applied client-side without a server round-trip.
 * Structural changes require fetching new data from the server.
 *
 * Classification logic (centralized — no manual whitelists needed):
 * 1. Appearance props (_bg, _textColor, _padding, _radius, _maxWidth) → always cosmetic
 * 2. Text-type slots (heading, subheading, body, etc.) → cosmetic (auto-derived from WIDGET_REGISTRY)
 * 3. Props with `structural: true` in widget definition → structural
 * 4. Everything else → cosmetic (safe default — most props are UI-only)
 *
 * To make a new prop structural, add `structural: true` to the prop definition
 * in the widget's defineWidget() call. No other code changes needed.
 */

import { WIDGET_REGISTRY } from '../schema/widget-definitions';
import type { WidgetDefinition } from '../schema/widget-types';

// Appearance props are ALWAYS cosmetic for ALL section types
export const COSMETIC_APPEARANCE_PROPS = ['_bg', '_textColor', '_padding', '_radius', '_maxWidth'] as const;

const APPEARANCE_SET = new Set<string>(COSMETIC_APPEARANCE_PROPS);

/**
 * Derive text slot keys from WIDGET_REGISTRY.
 * A slot with type 'text' means its corresponding prop key is cosmetic.
 */
function buildTextPropsMap(
  registry: Record<string, WidgetDefinition>
): Record<string, Set<string>> {
  const map: Record<string, Set<string>> = {};
  for (const [widgetType, def] of Object.entries(registry)) {
    const textKeys = new Set<string>();
    if (def.schema.slots) {
      for (const [slotKey, slotDef] of Object.entries(def.schema.slots)) {
        if (slotDef.type === 'text') {
          textKeys.add(slotKey);
        }
      }
    }
    map[widgetType] = textKeys;
  }
  return map;
}

/**
 * Build a set of structural prop keys per widget type from WIDGET_REGISTRY.
 * Only props with `structural: true` in their definition are included.
 */
function buildStructuralPropsMap(
  registry: Record<string, WidgetDefinition>
): Record<string, Set<string>> {
  const map: Record<string, Set<string>> = {};
  for (const [widgetType, def] of Object.entries(registry)) {
    const structuralKeys = new Set<string>();
    if (def.schema.props) {
      for (const [propKey, propDef] of Object.entries(def.schema.props)) {
        if (propDef.structural) {
          structuralKeys.add(propKey);
        }
      }
    }
    map[widgetType] = structuralKeys;
  }
  return map;
}

/** Per-widget-type map of text prop keys derived from slot definitions */
export const COSMETIC_TEXT_PROPS = buildTextPropsMap(WIDGET_REGISTRY);

/** Per-widget-type map of structural prop keys derived from widget definitions */
const STRUCTURAL_PROPS = buildStructuralPropsMap(WIDGET_REGISTRY);

/**
 * Classify a prop change as cosmetic or structural.
 *
 * Logic (in order):
 * 1. Appearance props (_bg, _textColor, etc.) → always cosmetic
 * 2. Text slots (heading, subheading, etc.) → cosmetic
 * 3. Props marked `structural: true` in widget definition → structural
 * 4. Everything else → cosmetic (default for UI-only props)
 *
 * @param sectionType - Widget type key (e.g., 'hero', 'product-grid')
 * @param propKey - The prop key being changed (e.g., '_bg', 'heading', 'collectionSlug')
 * @returns 'cosmetic' if the change can be applied client-side, 'structural' if it needs a server call
 */
export function classifyChange(
  sectionType: string,
  propKey: string
): 'cosmetic' | 'structural' {
  // Appearance props are always cosmetic regardless of widget type
  if (APPEARANCE_SET.has(propKey)) return 'cosmetic';

  // Check if this prop is a text slot for this widget type
  const textProps = COSMETIC_TEXT_PROPS[sectionType];
  if (textProps && textProps.has(propKey)) return 'cosmetic';

  // Check if this prop is explicitly marked structural in the widget definition
  const structuralProps = STRUCTURAL_PROPS[sectionType];
  if (structuralProps && structuralProps.has(propKey)) return 'structural';

  // Default: cosmetic — most props only affect presentation, not data queries
  return 'cosmetic';
}

/**
 * Classify a block content property change as cosmetic or structural.
 *
 * All block content changes (text value, image src, button label, etc.)
 * are cosmetic — they only affect presentation and don't require server
 * data fetching. Block insert/delete/reorder are structural (handled by
 * separate PostMessage types in Epic 02/03).
 */
export function classifyBlockChange(
  _blockType: string,
  _contentKey: string,
): 'cosmetic' | 'structural' {
  return 'cosmetic';
}
